// @ts-check
// Inserir leads num fluxo (spec 2.0.0, fluxos.md seção 20.15, item 1; contrato 09 de fluxos, seção 17). Pela lista dos
// fluxos (o fluxo já vem escolhido): uma view da escola ou um filtro, com a contagem, a tarefa em segundo plano e o
// resultado. Por uma seleção de cards ou por um card (comandos selection.addToFlow e record.addToFlow): escolher um
// fluxo ativo, "Incluir quem já percorreu" e o resultado; seleções acima de 200 vão em partes de 200 e "selecionar
// todos" de uma lista vira público. O diálogo abre fora da árvore da página (render num contêiner próprio).
import { html } from 'htm/preact';
import { render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getFlow, getFlowJob, insertLeadsIntoFlow, listFlows } from '../../data/flows.js';
import { objectsByNameSingular, viewsState } from '../../data/metadata-store.js';
import { Button } from '../../ui/button.js';
import { Checkbox, Skeleton } from '../../ui/feedback.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { formatNumber } from '../../lib/format.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { MAXIMUM_FLOW_INSERT_IDS } from '../../../../shared/flows/flow-limits.js';
import { audienceFromViewState, createEmptyAudienceDefinition } from '../../../../shared/audiences/audience-definition.js';
import { AudienceBuilder } from '../audiences/audience-builder.js';
import { getViewDraft } from '../views/view-state.js';
import { FlowDialog } from './flow-dialog.js';
import { FlowField, FlowNotice, flowReasonText } from './flow-ui.js';
import { translateMetadataLabel, t } from '../../i18n/index.js';

/**
 * @typedef {{ objectMetadata?: import('../../lib/field-metadata.js').ObjectMetadata, recordIds?: string[] | null,
 *   view?: import('../../data/metadata-store.js').View | null, flow?: { id: string, name: string } | null, onDone?: () => void }} FlowInsertLeadsRequest
 *   flow: pela lista dos fluxos (o público é escolhido no diálogo); recordIds: cards escolhidos (null com `view`: todos os da lista)
 * @typedef {{ inserted: number, skipped: number, skippedByReason: Record<string, number> }} InsertResult
 */

const JOB_POLL_MILLISECONDS = 1500;
const CARD_OBJECT_NAME = 'enrollmentOpportunity';

/** Cards em partes de 200. @param {string[]} ids @returns {string[][]} */
export function chunkRecordIds(ids) {
  /** @type {string[][]} */
  const chunks = [];
  for (let start = 0; start < ids.length; start += MAXIMUM_FLOW_INSERT_IDS) chunks.push(ids.slice(start, start + MAXIMUM_FLOW_INSERT_IDS));
  return chunks;
}

/**
 * Soma os resultados das partes (ids) ou da tarefa (público).
 * @param {Array<{ inserted?: Array<unknown>, skipped?: Array<{ reason: string }> }>} parts
 * @returns {InsertResult}
 */
export function sumInsertResults(parts) {
  /** @type {Record<string, number>} */
  const skippedByReason = {};
  let inserted = 0;
  let skipped = 0;
  for (const part of parts) {
    inserted += part.inserted?.length ?? 0;
    for (const item of part.skipped ?? []) {
      skipped += 1;
      skippedByReason[item.reason] = (skippedByReason[item.reason] ?? 0) + 1;
    }
  }
  return { inserted, skipped, skippedByReason };
}

/** Frase do resultado: "1.180 entraram; 54 ficaram de fora: 40 já estão no fluxo, 14 já percorreram". @param {InsertResult} result */
export function insertResultText(result) {
  const head = t('flows.insert.inserted', { count: result.inserted, number: formatNumber(result.inserted) });
  if (result.skipped === 0) return head;
  const reasons = Object.entries(result.skippedByReason).filter(([, count]) => count > 0)
    .map(([reason, count]) => `${formatNumber(count)} ${flowReasonText('ENTRY_SKIP', reason).toLocaleLowerCase('pt-BR')}`).join(', ');
  return `${head}; ${t('flows.insert.skipped', { count: result.skipped, number: formatNumber(result.skipped) })}${reasons ? `: ${reasons}` : ''}`;
}

let host = /** @type {HTMLElement | null} */ (null);

/** Abre o diálogo de inserir leads. @param {FlowInsertLeadsRequest} request */
export function openFlowInsertLeadsDialog(request) {
  if (!host) {
    host = document.createElement('div');
    host.setAttribute('data-flow-insert-host', 'true');
    document.body.appendChild(host);
  }
  const target = host;
  const close = () => render(null, target);
  render(html`<${FlowInsertLeadsDialog} key=${Date.now()} request=${request} onClose=${close} />`, target);
}

/**
 * @param {{ request: FlowInsertLeadsRequest, onClose: () => void }} props
 */
export function FlowInsertLeadsDialog({ request, onClose }) {
  const fromFlow = Boolean(request.flow);
  const [flows, setFlows] = useState(/** @type {Array<{ id: string, name: string }> | null} */ (fromFlow ? [] : null));
  const [flowId, setFlowId] = useState(request.flow?.id ?? '');
  const [search, setSearch] = useState('');
  const [reentry, setReentry] = useState(/** @type {'ONCE' | 'MULTIPLE' | null} */ (null));
  const [allowReentry, setAllowReentry] = useState(false);
  const [source, setSource] = useState(/** @type {'VIEW' | 'FILTER'} */ ('VIEW'));
  const cardViews = viewsState.value.filter((view) => view.objectMetadataId === objectsByNameSingular.value.get(CARD_OBJECT_NAME)?.id);
  const [viewId, setViewId] = useState(cardViews[0]?.id ?? '');
  const [audience, setAudience] = useState(() => createEmptyAudienceDefinition({ usage: 'AUDIENCE' }));
  const [state, setState] = useState(/** @type {'EDIT' | 'RUNNING' | 'DONE'} */ ('EDIT'));
  const [progress, setProgress] = useState({ processed: 0, total: /** @type {number | null} */ (null) });
  const [result, setResult] = useState(/** @type {InsertResult | null} */ (null));
  const isCancelledRef = useRef(false);

  useEffect(() => {
    if (fromFlow) return undefined;
    listFlows({ status: 'ACTIVE' }).then((result) => setFlows(result.flows.filter((flow) => flow.status === 'ACTIVE'))).catch(() => setFlows([]));
    return undefined;
  }, []);
  useEffect(() => {
    setReentry(null);
    if (!flowId) return;
    getFlow(flowId).then((flow) => setReentry(flow.definition.settings.reentry)).catch(() => setReentry(null));
  }, [flowId]);
  useEffect(() => () => {
    isCancelledRef.current = true;
  }, []);

  const recordIds = request.recordIds ?? null;
  const isAllOfList = !fromFlow && recordIds === null && Boolean(request.view);
  const selectedCount = recordIds?.length ?? null;
  const flowName = request.flow?.name ?? flows?.find((flow) => flow.id === flowId)?.name ?? '';

  /** Público da lista inteira aberta (os filtros do rascunho da view, com perdidos). */
  const listAudience = () => {
    const view = /** @type {import('../../data/metadata-store.js').View} */ (request.view);
    const draft = getViewDraft(view);
    const base = audienceFromViewState({ filters: draft.filters.value, filterGroups: draft.filterGroups.value, anyFieldSearch: draft.anyFieldSearch.value }, { usage: 'AUDIENCE' }).definition;
    return { ...base, viewId: view.id, presets: { ...base.presets, lost: 'INCLUDE' } };
  };

  /** @param {string} jobFlowId @param {string} jobId */
  const waitForJob = async (jobFlowId, jobId) => {
    for (;;) {
      if (isCancelledRef.current) return null;
      const job = await getFlowJob(jobFlowId, jobId);
      setProgress({ processed: job.processed, total: job.totalCards });
      if (job.status === 'DONE' || job.status === 'FAILED' || job.status === 'CANCELED') return job;
      await new Promise((resolve) => setTimeout(resolve, JOB_POLL_MILLISECONDS));
    }
  };

  const confirm = async () => {
    if (!flowId) return;
    setState('RUNNING');
    try {
      const reentryFlag = reentry === 'ONCE' ? allowReentry : undefined;
      if (fromFlow || isAllOfList) {
        const chosen = fromFlow ? (source === 'VIEW' ? { ...createEmptyAudienceDefinition({ usage: 'AUDIENCE' }), viewId } : audience) : listAudience();
        const response = await insertLeadsIntoFlow(flowId, { audience: chosen, allowReentry: reentryFlag });
        if (response.job) {
          const job = await waitForJob(flowId, response.job.id);
          if (!job) return;
          if (job.status === 'FAILED') throw new Error(job.error ?? 'FAILED');
          setResult({ inserted: job.inserted, skipped: job.skipped, skippedByReason: job.skippedByReason ?? {} });
        } else setResult(sumInsertResults([response]));
      } else {
        const ids = recordIds ?? [];
        /** @type {Array<any>} */
        const parts = [];
        const chunks = chunkRecordIds(ids);
        for (const [position, chunk] of chunks.entries()) {
          if (isCancelledRef.current) return;
          parts.push(await insertLeadsIntoFlow(flowId, { recordIds: chunk, allowReentry: reentryFlag }));
          setProgress({ processed: Math.min(ids.length, (position + 1) * MAXIMUM_FLOW_INSERT_IDS), total: ids.length });
        }
        setResult(sumInsertResults(parts));
      }
      setState('DONE');
      request.onDone?.();
    } catch (error) {
      showErrorSnackbar(error);
      setState('EDIT');
    }
  };

  const visibleFlows = (flows ?? []).filter((flow) => !search || normalizeSearchText(flow.name).includes(normalizeSearchText(search)));
  const title = fromFlow ? t('flows.insert.titleFor', { name: flowName }) : t('flows.insert.title');
  const footer = state === 'DONE'
    ? html`<${Button} title=${t('common.close')} accent="blue" size="small" onClick=${onClose} />`
    : html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.insert.confirm')} accent="blue" size="small" isLoading=${state === 'RUNNING'} disabled=${!flowId || (fromFlow && source === 'VIEW' && !viewId)}
        onClick=${confirm} data-testid="flow-insert-confirm" />`;

  return html`<${FlowDialog} title=${title} size=${fromFlow && source === 'FILTER' ? 'large' : 'medium'} onClose=${onClose} footer=${footer} testId="flow-insert-dialog">
    ${state === 'DONE' && result
      ? html`<div class="flow-insert-result" role="status"><p class="flow-dialog-text">${insertResultText(result)}</p></div>`
      : state === 'RUNNING'
        ? html`<div class="flow-insert-progress" role="status">
            <p class="flow-dialog-text">${progress.total ? t('flows.insert.progress', { processed: formatNumber(progress.processed), total: formatNumber(progress.total) }) : t('flows.insert.running')}</p>
            <div class="flow-progress-bar"><span style=${{ width: `${progress.total ? Math.min(100, (progress.processed / progress.total) * 100) : 15}%` }}></span></div>
          </div>`
        : html`
          ${!fromFlow
            ? html`<p class="flow-dialog-text">${isAllOfList ? t('flows.insert.allOfList') : t('flows.insert.selected', { count: selectedCount ?? 0, number: formatNumber(selectedCount ?? 0) })}</p>
              <${FlowField} label=${t('flows.insert.flow')} htmlFor="flow-insert-search">
                <input id="flow-insert-search" type="search" class="text-input" value=${search} placeholder=${t('flows.insert.searchFlow')}
                  onInput=${(/** @type {InputEvent} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
              <//>
              ${flows === null
                ? html`<${Skeleton} height=${120} />`
                : flows.length === 0
                  ? html`<${FlowNotice} icon="IconInfoCircle">${t('flows.insert.noActiveFlows')}<//>`
                  : html`<ul class="flow-insert-flows" role="radiogroup" aria-label=${t('flows.insert.flow')}>
                      ${visibleFlows.map((flow) => html`<li key=${flow.id}>
                        <label class="flow-radio-option" data-checked=${flowId === flow.id ? 'true' : undefined}>
                          <input type="radio" name="flow-insert-flow" value=${flow.id} checked=${flowId === flow.id} onChange=${() => setFlowId(flow.id)} />
                          <span class="flow-radio-title">${flow.name}</span>
                        </label>
                      </li>`)}
                    </ul>`}`
            : html`<${SegmentedControl} label=${t('flows.insert.source')} value=${source} onChange=${setSource}
                options=${[{ value: 'VIEW', label: t('flows.insert.sources.VIEW') }, { value: 'FILTER', label: t('flows.insert.sources.FILTER') }]} />
              ${source === 'VIEW'
                ? html`<${FlowField} label=${t('flows.insert.view')} htmlFor="flow-insert-view">
                    <select id="flow-insert-view" class="text-input" value=${viewId} onChange=${(/** @type {Event} */ event) => setViewId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                      ${cardViews.map((view) => html`<option key=${view.id} value=${view.id}>${translateMetadataLabel(view.name)}</option>`)}
                    </select>
                  <//>`
                : html`<${AudienceBuilder} value=${audience} usage="AUDIENCE" channels=${[]} showRecipients=${false} onChange=${setAudience} />`}`}
          ${reentry === 'ONCE'
            ? html`<label class="flow-checkbox-row">
                <${Checkbox} checked=${allowReentry} onChange=${(/** @type {boolean} */ value) => setAllowReentry(value)} ariaLabel=${t('flows.insert.allowReentry')} />
                <span>${t('flows.insert.allowReentry')}</span>
              </label>`
            : null}`}
  <//>`;
}
